import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "./spinner";

/**
 * Buttons follow the lean2automate site:
 *  - primary: ink pill that turns teal on hover (site .nav-cta)
 *  - brand:   teal→blue gradient (site .button.primary) — hero / auth CTAs only
 *  - secondary: white pill with line-strong border, hover blue (site .button.secondary)
 * One primary button per region. Labels are verbs ("Issue invoice").
 */
export const buttonVariants = cva(
  "relative inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-pill font-bold transition-[background-color,color,border-color,box-shadow,transform] duration-150 ease-out disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-ink text-ink-fg shadow-xs hover:bg-ink-hover hover:text-ink-hover-fg active:translate-y-px",
        brand: "bg-brand-gradient text-white shadow-glow hover:shadow-md hover:brightness-[1.06] active:translate-y-px",
        secondary: "border border-line-strong bg-surface text-fg shadow-xs hover:border-accent hover:text-accent-ink dark:hover:text-accent",
        outline: "border border-line-strong bg-transparent text-fg hover:bg-surface-hover",
        ghost: "text-fg-soft hover:bg-fg/[0.06] hover:text-fg data-[state=open]:bg-fg/[0.06] data-[state=open]:text-fg",
        soft: "bg-primary-soft text-primary-ink hover:bg-primary-soft-hover",
        danger: "bg-danger-solid text-white shadow-xs hover:bg-danger-solid-hover active:translate-y-px",
        "danger-ghost": "text-danger-ink hover:bg-danger-soft",
        link: "h-auto rounded-none px-0 text-accent-ink underline-offset-4 hover:underline dark:text-accent",
      },
      size: {
        xs: "h-7 px-2.5 text-xs [&_svg]:size-3.5",
        sm: "h-8 px-3.5 text-sm [&_svg]:size-4",
        md: "h-9 px-4 text-base [&_svg]:size-4",
        lg: "h-11 px-6 text-md [&_svg]:size-[18px]",
      },
      block: { true: "w-full" },
    },
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  /** Text shown while loading, e.g. "Issuing…" (defaults to children). */
  loadingText?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, asChild, loading, loadingText, leftIcon, rightIcon, disabled, children, type, ...props },
  ref,
) {
  if (asChild) {
    // `Slottable` marks which child the caller's element replaces, so the icons
    // still render *inside* that element (a <Link>, usually). Without it Slot
    // demands a single child and the icons would have to be dropped.
    return (
      <Slot.Root ref={ref} className={cn(buttonVariants({ variant, size, block }), className)} {...props}>
        {leftIcon}
        <Slot.Slottable>{children}</Slot.Slottable>
        {rightIcon}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(buttonVariants({ variant, size, block }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Spinner label="" className="size-4" /> : leftIcon}
      {loading && loadingText ? loadingText : children}
      {!loading && rightIcon}
    </button>
  );
});

export const iconButtonVariants = cva(
  "inline-flex shrink-0 items-center justify-center rounded-control transition-colors duration-150 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        ghost: "text-fg-muted hover:bg-fg/[0.06] hover:text-fg data-[state=open]:bg-fg/[0.06] data-[state=open]:text-fg",
        outline: "border border-line-strong bg-surface text-fg-soft shadow-xs hover:border-control hover:text-fg",
        soft: "bg-primary-soft text-primary-ink hover:bg-primary-soft-hover",
        primary: "bg-ink text-ink-fg hover:bg-ink-hover hover:text-ink-hover-fg",
        danger: "text-danger-ink hover:bg-danger-soft",
      },
      size: {
        xs: "size-6 rounded-item [&_svg]:size-3.5",
        sm: "size-8 [&_svg]:size-4",
        md: "size-9 [&_svg]:size-[18px]",
        lg: "size-11 [&_svg]:size-5",
      },
      round: { true: "rounded-pill" },
    },
    defaultVariants: { variant: "ghost", size: "md" },
  },
);

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof iconButtonVariants> {
  /** Required accessible name (icon-only buttons have no visible text). */
  "aria-label": string;
  asChild?: boolean;
  loading?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { className, variant, size, round, asChild, loading, children, type, disabled, ...props },
  ref,
) {
  const classes = cn(iconButtonVariants({ variant, size, round }), className);
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button ref={ref} type={type ?? "button"} className={classes} disabled={disabled || loading} {...props}>
      {loading ? <Spinner label="" /> : children}
    </button>
  );
});
